import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ShareIcon,
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  ChatBubbleBottomCenterTextIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";
import { Menu } from "@headlessui/react";
import Image from "next/image";
import { useToast } from "@/contexts/ToastContext";
import { useProductSettings } from "@/lib/hooks/useProductSettings";
import { generateShareText } from "@/lib/shareTemplateService";
import { formatPrice } from "@/lib/utils";
import Modal from "@/components/ui/Modal";
import {
  composeSocialGridImage,
  DEFAULT_SOCIAL_GRID_SIZE,
} from "@/lib/socialGridComposer";
import {
  buildPartnerAssetKitCaption,
  buildPartnerMessage,
  buildWhatsappShareUrl,
} from "@/lib/partnerShareTemplates";

const shareMenuAnchor = {
  to: "bottom end" as const,
  gap: "6px" as const,
  padding: "12px" as const,
};

/** Above admin modal shell (`Modal` uses z-[10050]). */
const SHARE_MENU_PORTAL_Z = "z-[10060]";

interface ShareItemProps {
  title: string;
  price: number;
  location: string;
  defects?: string;
  description?: string;
  mainImageUrl?: string;
  imageUrls?: string[];
  /** Canonical share URL (for partners should include ?ref=). */
  url: string;
  /** Extra tools: referral templates, WhatsApp, promo pack, web share. */
  partnerToolsEnabled?: boolean;
  /** Smaller control for tight layouts (e.g. item cards). */
  compact?: boolean;
  /** One-click Copy link / WhatsApp / Copy text beside the share menu. */
  quickActions?: boolean;
  /** Load share text templates when mounted (faster first copy). */
  prefetchSettings?: boolean;
  /** Show “Copy listing text” in the share menu (admin surfaces). */
  showCopyListingText?: boolean;
}

export default function ShareItem({
  title,
  price,
  location,
  defects = "",
  description = "",
  mainImageUrl,
  imageUrls = [],
  url,
  partnerToolsEnabled = false,
  compact = false,
  quickActions = false,
  prefetchSettings = false,
  showCopyListingText = false,
}: ShareItemProps) {
  const { showToast } = useToast();
  const [shouldFetchSettings, setShouldFetchSettings] = useState(false);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isCopyingImage, setIsCopyingImage] = useState(false);
  const [isCopyingText, setIsCopyingText] = useState(false);
  const [isPromoPackLoading, setIsPromoPackLoading] = useState(false);
  const [includeWatermark, setIncludeWatermark] = useState(true);
  const [includeOverlayLogo, setIncludeOverlayLogo] = useState(true);
  const [isCollageModalOpen, setIsCollageModalOpen] = useState(false);
  const [collageSelectedIndices, setCollageSelectedIndices] = useState<
    number[]
  >([]);
  const [isComposingCollage, setIsComposingCollage] = useState(false);
  const [collageBlob, setCollageBlob] = useState<Blob | null>(null);
  const [collagePreviewUrl, setCollagePreviewUrl] = useState<string | null>(
    null,
  );
  const [collageTwoOrientation, setCollageTwoOrientation] = useState<
    "horizontal" | "vertical"
  >("horizontal");
  const [isSharingCollage, setIsSharingCollage] = useState(false);
  const [isCopyingCollageCaption, setIsCopyingCollageCaption] = useState(false);

  const { settings, isLoading, refreshSettings } =
    useProductSettings(prefetchSettings);

  const partnerFields = useMemo(
    () => ({ title, price, location, defects, url }),
    [title, price, location, defects, url],
  );

  const getShareText = () => {
    if (!isLoading && settings.shareTextTemplate) {
      try {
        return generateShareText(
          settings.shareTextTemplate.type,
          {
            title,
            price,
            location,
            description,
            defects,
            url,
          },
          settings.shareTextTemplate.customTemplate ?? undefined,
          {
            option_a: settings.shareTextTemplate.optionATemplate ?? undefined,
            option_b: settings.shareTextTemplate.optionBTemplate ?? undefined,
          },
          {
            locationMode: settings.shareTextTemplate.locationMode ?? "full",
          },
        );
      } catch (error) {
        console.error("Error generating share text from template:", error);
      }
    }

    const formattedPrice = formatPrice(price);
    const defectsText = defects?.trim() ? `Defects: ${defects}` : "No defects";
    return `*${title}* for sale on Sell It Off!\n\n* *Price:*${formattedPrice}\n* *Location:* ${location}\n* *Defects:* ${defectsText}\n\nView it here: ${url}`;
  };

  const shareText = partnerToolsEnabled
    ? buildPartnerMessage("long", partnerFields)
    : getShareText();
  const shouldShowBrandingControls = !partnerToolsEnabled;
  const applyWatermark = partnerToolsEnabled || includeWatermark;
  const applyOverlayLogo = partnerToolsEnabled || includeOverlayLogo;
  const showListingTextInMenu = showCopyListingText || partnerToolsEnabled;
  const quickActionButtonClass =
    "inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-foreground shadow-sm hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";

  const availableImageUrls = useMemo(() => {
    const seen = new Set<string>();
    const values = [...imageUrls];
    if (mainImageUrl) {
      values.unshift(mainImageUrl);
    }
    return values
      .map((u) => u?.trim())
      .filter((u): u is string => Boolean(u))
      .filter((u) => {
        if (seen.has(u)) return false;
        seen.add(u);
        return true;
      });
  }, [imageUrls, mainImageUrl]);

  const collageEnabled = availableImageUrls.length > 0;
  const collageMaxSelectable = 6;
  const collageSelectedUrls = useMemo(
    () =>
      collageSelectedIndices
        .map((idx) => availableImageUrls[idx])
        .filter((u): u is string => Boolean(u)),
    [collageSelectedIndices, availableImageUrls],
  );
  const collageSelectedSet = useMemo(
    () => new Set(collageSelectedIndices),
    [collageSelectedIndices],
  );
  const collageRows = useMemo(() => {
    const selected = collageSelectedIndices.map((imageIndex, selectedPos) => ({
      imageIndex,
      selectedPos,
      selected: true,
    }));
    const unselected = availableImageUrls
      .map((_, imageIndex) => imageIndex)
      .filter((imageIndex) => !collageSelectedSet.has(imageIndex))
      .map((imageIndex) => ({
        imageIndex,
        selectedPos: -1,
        selected: false,
      }));
    return [...selected, ...unselected];
  }, [availableImageUrls, collageSelectedIndices, collageSelectedSet]);
  const logoAssetUrl = "/images/logo.svg";

  useEffect(() => {
    if (shouldFetchSettings) {
      refreshSettings();
    }
  }, [shouldFetchSettings, refreshSettings]);

  useEffect(() => {
    if (!isCollageModalOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      setCollageTwoOrientation("horizontal");
      const seedCount = Math.min(
        collageMaxSelectable,
        availableImageUrls.length,
      );
      setCollageSelectedIndices(Array.from({ length: seedCount }, (_, i) => i));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isCollageModalOpen, availableImageUrls.length]);

  useEffect(() => {
    if (!isCollageModalOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      setCollageSelectedIndices((prev) => {
        const filtered = prev.filter(
          (idx) => idx >= 0 && idx < availableImageUrls.length,
        );
        return filtered.length === prev.length ? prev : filtered;
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isCollageModalOpen, availableImageUrls.length]);

  useEffect(() => {
    if (!isCollageModalOpen || collageSelectedUrls.length === 0) {
      const timer = window.setTimeout(() => {
        setIsComposingCollage(false);
        setCollageBlob(null);
        setCollagePreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      }, 0);
      return () => window.clearTimeout(timer);
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      setIsComposingCollage(true);
      setCollageBlob(null);
      setCollagePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });

      composeSocialGridImage(collageSelectedUrls, {
        size: DEFAULT_SOCIAL_GRID_SIZE,
        twoImageOrientation:
          collageSelectedUrls.length === 2 ? collageTwoOrientation : undefined,
        cellLabels: collageSelectedUrls.map(() => ({
          title,
          priceLocationLine: `${formatPrice(price)} | ${location?.trim() || "—"}`,
        })),
        branding: {
          showWatermark: applyWatermark,
          showOverlayLogo: applyOverlayLogo,
          logoUrl: logoAssetUrl,
        },
      })
        .then((blob) => {
          if (cancelled) return;
          setCollageBlob(blob);
          setCollagePreviewUrl(URL.createObjectURL(blob));
          setIsComposingCollage(false);
        })
        .catch((error) => {
          console.error("Collage compose failed:", error);
          if (!cancelled) {
            setCollageBlob(null);
            setIsComposingCollage(false);
            showToast(
              "Could not build collage. Check image URLs and try again.",
              "error",
            );
          }
        });
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    isCollageModalOpen,
    collageSelectedUrls,
    collageTwoOrientation,
    title,
    price,
    location,
    applyWatermark,
    applyOverlayLogo,
    showToast,
  ]);

  useEffect(() => {
    return () => {
      setCollagePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  const writeClipboard = useCallback(
    async (text: string, successMessage: string) => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          const successful = document.execCommand("copy");
          textArea.remove();
          if (!successful) {
            throw new Error("execCommand copy failed");
          }
        }
        showToast(successMessage, "success");
      } catch (err) {
        console.error("Clipboard error:", err);
        showToast("Failed to copy. Please try again.", "error");
      }
    },
    [showToast],
  );

  const buildBrandedSingleImageBlob = useCallback(async () => {
    if (!mainImageUrl) {
      throw new Error("No image");
    }
    return composeSocialGridImage([mainImageUrl], {
      size: DEFAULT_SOCIAL_GRID_SIZE,
      cellLabels: [
        {
          title,
          priceLocationLine: `${formatPrice(price)} | ${location?.trim() || "—"}`,
        },
      ],
      branding: {
        showWatermark: applyWatermark,
        showOverlayLogo: applyOverlayLogo,
        logoUrl: logoAssetUrl,
      },
    });
  }, [
    mainImageUrl,
    title,
    price,
    location,
    applyWatermark,
    applyOverlayLogo,
    logoAssetUrl,
  ]);

  const downloadImageToDevice = async (blob?: Blob) => {
    const imageBlob = blob ?? (await buildBrandedSingleImageBlob());
    const blobUrl = URL.createObjectURL(imageBlob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${title.replace(/[^a-z0-9]/gi, "_")}.jpg`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadImage = async () => {
    if (!mainImageUrl) {
      showToast("No image available to download", "error");
      return;
    }

    setIsDownloadingImage(true);
    try {
      const blob = await buildBrandedSingleImageBlob();
      await downloadImageToDevice(blob);
      showToast("Image downloaded successfully!", "success");
    } catch (error) {
      console.error("Error downloading image:", error);
      showToast("Failed to download image. Please try again.", "error");
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const handleCopyImage = async () => {
    if (!mainImageUrl) {
      showToast("No image available to copy", "error");
      return;
    }

    setIsCopyingImage(true);
    try {
      const blob = await buildBrandedSingleImageBlob();

      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          [blob.type]: blob,
        });
        await navigator.clipboard.write([clipboardItem]);
        showToast("Image copied to clipboard!", "success");
      } else {
        showToast("Image copy not supported. Downloading instead...", "info");
        await downloadImageToDevice(blob);
        showToast("Image downloaded successfully!", "success");
      }
    } catch (error) {
      console.error("Error copying image:", error);
      showToast("Copy failed. Downloading instead...", "info");
      const blob = await buildBrandedSingleImageBlob();
      await downloadImageToDevice(blob);
      showToast("Image downloaded successfully!", "success");
    } finally {
      setIsCopyingImage(false);
    }
  };

  const handleCopyText = async () => {
    if (!shouldFetchSettings) {
      setShouldFetchSettings(true);
    }

    setIsCopyingText(true);
    try {
      const shareText = getShareText();
      await writeClipboard(shareText, "Item details copied to clipboard!");
    } finally {
      setIsCopyingText(false);
    }
  };

  const handlePrimaryShare = async () => {
    const text = shareText;
    const shouldAttachUrl = Boolean(url && !text.includes(url));
    if (navigator.share) {
      try {
        await navigator.share(
          shouldAttachUrl ? { title, text, url } : { title, text },
        );
        return;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
      }
    }
    await writeClipboard(text, "Copied — paste to share");
  };

  const handleToggleCollageIndex = useCallback(
    (idx: number) => {
      setCollageSelectedIndices((prev) => {
        if (prev.includes(idx)) {
          return prev.filter((x) => x !== idx);
        }
        if (prev.length >= collageMaxSelectable) {
          showToast(
            `You can select up to ${collageMaxSelectable} images.`,
            "info",
          );
          return prev;
        }
        return [...prev, idx];
      });
    },
    [showToast],
  );

  const reorderSelectedIndex = useCallback((from: number, to: number) => {
    if (from === to) return;
    setCollageSelectedIndices((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, []);

  const handleDownloadCollage = useCallback(() => {
    if (!collagePreviewUrl) {
      showToast("Collage is not ready yet.", "error");
      return;
    }
    const filename = `${title.replace(/[^a-z0-9]/gi, "_")}_collage_${new Date().toISOString().slice(0, 10)}.jpg`;
    const link = document.createElement("a");
    link.href = collagePreviewUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    showToast("Collage download started", "success");
  }, [collagePreviewUrl, title, showToast]);

  const handleCopyCollageCaption = useCallback(async () => {
    if (!shareText.trim()) {
      showToast("Nothing to copy", "error");
      return;
    }
    setIsCopyingCollageCaption(true);
    try {
      await writeClipboard(shareText, "Caption copied to clipboard!");
    } finally {
      setIsCopyingCollageCaption(false);
    }
  }, [shareText, writeClipboard, showToast]);

  const handleShareCollage = useCallback(async () => {
    if (!collageBlob) {
      showToast("Collage is not ready yet.", "error");
      return;
    }
    const filename = `${title.replace(/[^a-z0-9]/gi, "_")}_collage.jpg`;
    const file = new File([collageBlob], filename, {
      type: collageBlob.type || "image/jpeg",
    });
    const filesOnly: ShareData = { files: [file] };
    if (!navigator.canShare?.(filesOnly)) {
      showToast(
        "Sharing isn’t supported in this browser. Use Download collage.",
        "info",
      );
      return;
    }
    const text = shareText.trim();
    const withText: ShareData = text ? { files: [file], text } : filesOnly;
    const useWithText = !text || (navigator.canShare?.(withText) ?? false);
    const payload = useWithText ? withText : filesOnly;
    setIsSharingCollage(true);
    try {
      await navigator.share(payload);
      if (!useWithText && text) {
        showToast(
          "Image shared. Paste caption from preview if your app didn’t include it.",
          "info",
        );
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      console.error("Collage share failed:", error);
      showToast("Could not open share sheet. Try Download collage.", "error");
    } finally {
      setIsSharingCollage(false);
    }
  }, [collageBlob, shareText, showToast, title]);

  const handleWhatsapp = () => {
    if (partnerToolsEnabled) {
      const text = buildPartnerMessage("whatsapp", partnerFields);
      window.open(buildWhatsappShareUrl(text), "_blank");
      return;
    }
    window.open(buildWhatsappShareUrl(getShareText()), "_blank");
  };

  const handleDownloadPromoPack = async () => {
    if (!mainImageUrl) {
      showToast("No image for this listing", "error");
      return;
    }
    setIsPromoPackLoading(true);
    try {
      const blob = await buildBrandedSingleImageBlob();
      await downloadImageToDevice(blob);
      const caption = buildPartnerAssetKitCaption(partnerFields);
      await writeClipboard(
        caption,
        "Promo pack ready — image saved, caption copied",
      );
    } catch (error) {
      console.error("Promo pack error:", error);
      showToast("Could not complete promo pack", "error");
    } finally {
      setIsPromoPackLoading(false);
    }
  };

  const menuWidth = "w-56 max-h-[min(80vh,420px)] overflow-y-auto";

  const shareMenu = (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className={
          compact
            ? "inline-flex items-center rounded-md border border-border bg-surface/95 p-1.5 text-foreground shadow-sm hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
            : "inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground bg-surface border border-border rounded-md hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        }
        title={quickActions ? "More share options" : "Share this item"}
        aria-label={quickActions ? "More share options" : "Share this item"}
      >
        <ShareIcon className={compact ? "h-4 w-4" : "w-5 h-5"} />
        {quickActions ? (
          <span className="sr-only">More share options</span>
        ) : null}
      </Menu.Button>

      <Menu.Items
        portal={compact || quickActions}
        anchor={compact || quickActions ? shareMenuAnchor : false}
        transition
        className={`origin-top-right rounded-md bg-surface shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${menuWidth} ${
          compact || quickActions
            ? SHARE_MENU_PORTAL_Z
            : "absolute right-0 z-[200] mt-2"
        }`}
      >
        <div className="py-1">
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => void handlePrimaryShare()}
                className={`${
                  active ? "bg-secondary text-foreground" : "text-foreground"
                } group flex w-full items-center gap-3 px-4 py-2 text-sm font-medium`}
              >
                <ShareIcon className="h-5 w-5 shrink-0 text-text-light" />
                Share…
              </button>
            )}
          </Menu.Item>
          {!quickActions ? (
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => void writeClipboard(url, "Link copied")}
                  className={`${
                    active ? "bg-secondary text-foreground" : "text-foreground"
                  } group flex w-full items-center gap-3 px-4 py-2 text-sm`}
                >
                  <DocumentDuplicateIcon className="h-5 w-5 shrink-0 text-text-light" />
                  Copy link
                </button>
              )}
            </Menu.Item>
          ) : null}

          <div className="my-1 border-t border-border" />
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-light">
            More
          </div>

          {!quickActions ? (
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={handleWhatsapp}
                  className={`${
                    active ? "bg-secondary text-foreground" : "text-foreground"
                  } group flex w-full items-center gap-3 px-4 py-2 text-sm`}
                >
                  <ChatBubbleBottomCenterTextIcon className="h-5 w-5 shrink-0 text-text-light" />
                  WhatsApp
                </button>
              )}
            </Menu.Item>
          ) : null}

          {shouldShowBrandingControls ? (
            <>
              <div className="my-1 border-t border-border" />
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-light">
                Branding
              </div>
              <div className="space-y-2 px-3 py-1.5">
                <label className="flex items-start gap-2 text-xs text-foreground">
                  <input
                    type="checkbox"
                    checked={includeWatermark}
                    onChange={(e) => setIncludeWatermark(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Add subtle Sell It Off watermark</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-foreground">
                  <input
                    type="checkbox"
                    checked={includeOverlayLogo}
                    onChange={(e) => setIncludeOverlayLogo(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Add logo in overlay bar</span>
                </label>
              </div>
            </>
          ) : null}

          {partnerToolsEnabled && mainImageUrl ? (
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => void handleDownloadPromoPack()}
                  disabled={isPromoPackLoading}
                  className={`${
                    active ? "bg-secondary text-foreground" : "text-foreground"
                  } group flex w-full items-center gap-3 px-4 py-2 text-sm disabled:opacity-50`}
                >
                  <ArrowDownTrayIcon className="h-5 w-5 shrink-0 text-text-light" />
                  {isPromoPackLoading ? "Working…" : "Promo pack"}
                </button>
              )}
            </Menu.Item>
          ) : null}

          {mainImageUrl ? (
            <>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => setIsCollageModalOpen(true)}
                    disabled={!collageEnabled}
                    className={`${
                      active
                        ? "bg-secondary text-foreground"
                        : "text-foreground"
                    } group flex w-full items-center gap-3 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <Squares2X2Icon className="h-5 w-5 shrink-0 text-text-light" />
                    Create collage
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => void handleDownloadImage()}
                    disabled={isDownloadingImage}
                    className={`${
                      active
                        ? "bg-secondary text-foreground"
                        : "text-foreground"
                    } group flex w-full items-center gap-3 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <ArrowDownTrayIcon className="h-5 w-5 shrink-0 text-text-light" />
                    {isDownloadingImage ? "Downloading…" : "Download photo"}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => void handleCopyImage()}
                    disabled={isCopyingImage}
                    className={`${
                      active
                        ? "bg-secondary text-foreground"
                        : "text-foreground"
                    } group flex w-full items-center gap-3 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <DocumentDuplicateIcon className="h-5 w-5 shrink-0 text-text-light" />
                    {isCopyingImage ? "Copying…" : "Copy photo"}
                  </button>
                )}
              </Menu.Item>
            </>
          ) : null}

          {showListingTextInMenu && !quickActions ? (
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => void handleCopyText()}
                  disabled={isCopyingText}
                  className={`${
                    active ? "bg-secondary text-foreground" : "text-foreground"
                  } group flex w-full items-center gap-3 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  <DocumentDuplicateIcon className="h-5 w-5 shrink-0 text-text-light" />
                  {isCopyingText ? "Copying…" : "Copy listing text"}
                </button>
              )}
            </Menu.Item>
          ) : null}
        </div>
      </Menu.Items>
      <Modal
        isOpen={isCollageModalOpen}
        onClose={() => setIsCollageModalOpen(false)}
        title="Create item collage"
        maxWidth="4xl"
        bodyClassName="pr-1"
        footerClassName="!mt-0 border-t border-border bg-secondary pt-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-b-lg"
        footer={
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleDownloadCollage}
              disabled={!collagePreviewUrl || isComposingCollage}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary/10 px-4 py-2 text-sm font-medium text-primary-700 ring-1 ring-inset ring-primary/50 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <ArrowDownTrayIcon className="h-4 w-4 shrink-0" aria-hidden />
              Download collage
            </button>
            <button
              type="button"
              onClick={() => void handleCopyCollageCaption()}
              disabled={isCopyingCollageCaption || !shareText.trim()}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <DocumentDuplicateIcon className="h-4 w-4 shrink-0" aria-hidden />
              {isCopyingCollageCaption ? "Copying…" : "Copy caption"}
            </button>
            <button
              type="button"
              onClick={() => void handleShareCollage()}
              disabled={isSharingCollage || !collageBlob || isComposingCollage}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-surface px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <ShareIcon className="h-4 w-4 shrink-0" aria-hidden />
              {isSharingCollage ? "Sharing…" : "Share"}
            </button>
          </div>
        }
      >
        <div className="space-y-4 pb-1">
          <p className="text-sm text-text-light">
            Select up to {collageMaxSelectable} images, reorder them, and
            preview the collage live.
          </p>

          <div className="rounded-lg border border-border bg-secondary p-2">
            <div className="mb-2 flex items-center justify-between gap-2 px-1">
              <h4 className="text-sm font-medium text-foreground">
                Images for collage
              </h4>
              <span className="text-xs text-text-light">
                {collageSelectedUrls.length} selected
              </span>
            </div>
            <p className="mb-2 px-1 text-xs text-text-light">
              Use the checkbox to include/exclude. Reorder selected rows with
              drag or arrows.
            </p>
            <ul className="space-y-2">
              {collageRows.map(({ imageIndex, selectedPos, selected }) => {
                const src = availableImageUrls[imageIndex];
                return (
                  <li
                    key={`${imageIndex}-${selected ? "selected" : "unselected"}`}
                    draggable={selected}
                    onDragStart={(e) => {
                      if (!selected) return;
                      e.dataTransfer.setData("text/plain", String(selectedPos));
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      if (!selected) return;
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      if (!selected) return;
                      e.preventDefault();
                      const from = parseInt(
                        e.dataTransfer.getData("text/plain"),
                        10,
                      );
                      if (!Number.isNaN(from)) {
                        reorderSelectedIndex(from, selectedPos);
                      }
                    }}
                    className={`flex items-center gap-2 rounded-md border px-2 py-1.5 shadow-sm ${
                      selected
                        ? "border-primary/40 bg-surface"
                        : "border-border bg-surface/70"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleToggleCollageIndex(imageIndex)}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <div className="flex shrink-0 flex-col gap-0.5">
                      <button
                        type="button"
                        aria-label="Move up"
                        disabled={!selected || selectedPos === 0}
                        onClick={() =>
                          reorderSelectedIndex(selectedPos, selectedPos - 1)
                        }
                        className="rounded p-0.5 text-text-light hover:bg-tertiary hover:text-foreground disabled:opacity-30"
                      >
                        <ChevronUpIcon className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        aria-label="Move down"
                        disabled={
                          !selected ||
                          selectedPos === collageSelectedIndices.length - 1
                        }
                        onClick={() =>
                          reorderSelectedIndex(selectedPos, selectedPos + 1)
                        }
                        className="rounded p-0.5 text-text-light hover:bg-tertiary hover:text-foreground disabled:opacity-30"
                      >
                        <ChevronDownIcon className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                    <Bars3Icon
                      className={`h-5 w-5 shrink-0 ${
                        selected
                          ? "cursor-grab text-text-light active:cursor-grabbing"
                          : "text-text-light"
                      }`}
                      aria-hidden
                    />
                    <Image
                      src={src}
                      alt=""
                      width={40}
                      height={40}
                      unoptimized
                      className="h-10 w-10 shrink-0 rounded bg-tertiary object-cover"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                      Image {imageIndex + 1}
                    </span>
                    {!selected ? (
                      <span className="text-[11px] text-text-light">
                        not included
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-medium text-foreground">
              Collage preview
            </h4>
            {collageSelectedUrls.length === 2 ? (
              <div className="mb-2 inline-flex overflow-hidden rounded-md border border-border bg-surface">
                <button
                  type="button"
                  onClick={() => setCollageTwoOrientation("horizontal")}
                  className={`px-3 py-1.5 text-xs font-medium ${
                    collageTwoOrientation === "horizontal"
                      ? "bg-primary/10 text-primary-700"
                      : "text-foreground hover:bg-secondary"
                  }`}
                >
                  Left to right
                </button>
                <button
                  type="button"
                  onClick={() => setCollageTwoOrientation("vertical")}
                  className={`border-l border-border px-3 py-1.5 text-xs font-medium ${
                    collageTwoOrientation === "vertical"
                      ? "bg-primary/10 text-primary-700"
                      : "text-foreground hover:bg-secondary"
                  }`}
                >
                  Top to bottom
                </button>
              </div>
            ) : null}
            <div
              className="relative mx-auto aspect-square max-w-lg overflow-hidden rounded-lg border border-border bg-secondary"
              aria-busy={isComposingCollage}
            >
              {collagePreviewUrl ? (
                <Image
                  src={collagePreviewUrl}
                  alt="Collage preview"
                  width={DEFAULT_SOCIAL_GRID_SIZE}
                  height={DEFAULT_SOCIAL_GRID_SIZE}
                  unoptimized
                  className="h-full w-full object-contain"
                />
              ) : (
                <div
                  className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 text-sm text-text-light"
                  role="status"
                  aria-live="polite"
                >
                  <span
                    className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
                    aria-hidden
                  />
                  <span>
                    {collageSelectedUrls.length === 0
                      ? "Select at least one image."
                      : "Composing…"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-medium text-foreground">
              Caption preview
            </h4>
            <pre className="max-h-44 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-secondary p-3 text-xs text-foreground">
              {shareText}
            </pre>
          </div>
        </div>
      </Modal>
    </Menu>
  );

  if (!quickActions) {
    return shareMenu;
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      <button
        type="button"
        className={quickActionButtonClass}
        onClick={() => void writeClipboard(url, "Link copied")}
      >
        <DocumentDuplicateIcon className="h-3.5 w-3.5 shrink-0 text-text-light" />
        Copy link
      </button>
      <button
        type="button"
        className={quickActionButtonClass}
        onClick={handleWhatsapp}
      >
        <ChatBubbleBottomCenterTextIcon className="h-3.5 w-3.5 shrink-0 text-text-light" />
        WhatsApp
      </button>
      <button
        type="button"
        className={quickActionButtonClass}
        onClick={() => void handleCopyText()}
        disabled={isCopyingText}
      >
        <DocumentDuplicateIcon className="h-3.5 w-3.5 shrink-0 text-text-light" />
        {isCopyingText ? "Copying…" : "Copy text"}
      </button>
      {shareMenu}
    </div>
  );
}
